// ─────────────────────────────────────────────────────────────────────────────
// POSE TRACKER — MediaPipe PoseLandmarker (33 body joints)
// Runtime: browser WASM via CDN (no server required)
// Degrades gracefully: if load fails, returns null landmarks
// ─────────────────────────────────────────────────────────────────────────────
import {
  PoseLandmarker,
  FilesetResolver,
  type PoseLandmarkerResult,
} from "@mediapipe/tasks-vision";

export type { PoseLandmarkerResult };

export interface NormalizedLandmark {
  x: number; // 0–1 normalized
  y: number;
  z: number;
  visibility?: number;
}

// MediaPipe Pose landmark indices (subset we care about)
export const POSE_IDX = {
  NOSE: 0,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
} as const;

export let _landmarker: PoseLandmarker | null = null;
let _loading = false;
let _failed = false;

export async function getPoseLandmarker(): Promise<PoseLandmarker | null> {
  if (_landmarker) return _landmarker;
  if (_failed) return null;
  if (_loading) {
    // Wait for existing load
    await new Promise<void>(res => {
      const iv = setInterval(() => {
        if (!_loading) { clearInterval(iv); res(); }
      }, 100);
    });
    return _landmarker;
  }

  _loading = true;
  try {
    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm"
    );
    _landmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    console.log("[AR] PoseLandmarker ready ✓");
  } catch (err) {
    console.warn("[AR] PoseLandmarker failed to load — falling back to heuristic placement", err);
    _failed = true;
  }
  _loading = false;
  return _landmarker;
}

export function closePoseLandmarker() {
  _landmarker?.close();
  _landmarker = null;
  _failed = false;
}

// ── Derived geometry helpers ──────────────────────────────────────────────────

export interface TorsoGeometry {
  /** Midpoint between shoulders, normalized 0–1 */
  shoulderMid: { x: number; y: number };
  /** Distance between shoulders, normalized */
  shoulderWidth: number;
  /** Midpoint between hips, normalized */
  hipMid: { x: number; y: number };
  /** Distance between hips, normalized */
  hipWidth: number;
  /** Torso height (shoulder to hip), normalized */
  torsoHeight: number;
  /** Confidence 0–1 */
  confidence: number;
}

export function extractTorsoGeometry(
  landmarks: NormalizedLandmark[]
): TorsoGeometry | null {
  const ls = landmarks[POSE_IDX.LEFT_SHOULDER];
  const rs = landmarks[POSE_IDX.RIGHT_SHOULDER];
  const lh = landmarks[POSE_IDX.LEFT_HIP];
  const rh = landmarks[POSE_IDX.RIGHT_HIP];

  if (!ls || !rs || !lh || !rh) return null;

  const minVis = Math.min(
    ls.visibility ?? 1,
    rs.visibility ?? 1,
    lh.visibility ?? 1,
    rh.visibility ?? 1
  );
  if (minVis < 0.3) return null;

  const shoulderMid = { x: (ls.x + rs.x) / 2, y: (ls.y + rs.y) / 2 };
  const hipMid = { x: (lh.x + rh.x) / 2, y: (lh.y + rh.y) / 2 };
  const shoulderWidth = Math.abs(rs.x - ls.x);
  const hipWidth = Math.abs(rh.x - lh.x);
  const torsoHeight = Math.abs(hipMid.y - shoulderMid.y);

  return { shoulderMid, shoulderWidth, hipMid, hipWidth, torsoHeight, confidence: minVis };
}
